import React, { useEffect, useMemo, useState } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Container,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	Switch,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { useLocation } from 'react-router-dom';
import { Layout } from 'features/common/components/Layout';
import routes from 'constants/routes';
import countries from 'features/travelIntelligence/countries.json';
import {
	DestinationOption,
	frequentDestinations,
	geocodingClient,
	requestTravelIntelligence,
	TravelIntelligenceError,
} from 'features/travelIntelligence/services';

type FieldType = 'text' | 'number' | 'date' | 'select' | 'switch' | 'destination' | 'country';

type FormValues = Record<string, any>;

type ToolField = {
	name: string;
	label: string;
	type: FieldType;
	required?: boolean;
	advanced?: boolean;
	helperText?: string;
	options?: { label: string; value: string | number }[];
	condition?: (values: FormValues) => boolean;
	uppercase?: boolean;
};

type ToolConfig = {
	id: string;
	label: string;
	path: string;
	description: string;
	method?: 'GET' | 'POST';
	endpoint: string;
	defaultValues: FormValues;
	fields?: ToolField[];
	buildRequest: (values: FormValues) => { query?: Record<string, unknown>; body?: unknown };
	customForm?: (props: ToolFormProps) => JSX.Element;
};

type ToolFormProps = {
	values: FormValues;
	setValue: (name: string, value: any) => void;
};

type PrimaryRule = {
	name?: string;
	duration?: string;
	color?: string;
};

type CountryOption = {
	code: string;
	name_short: string;
	name_es: string;
	name_en: string;
};

type ImportantFact = {
	label: string;
	value: string;
	highlight?: boolean;
};

type OfficialLink = {
	label: string;
	url: string;
};

type TravelerDocument = {
	passengerRef: string;
	passportCountry: string;
	residenceCountry: string;
	documentType: string;
	age: string;
};

const purposeOptions = [
	{ label: 'Turismo', value: 'tourism' },
	{ label: 'Negocios', value: 'business' },
	{ label: 'Tránsito', value: 'transit' },
	{ label: 'No definido', value: 'unknown' },
];

const languageOptions = [
	{ label: 'Español', value: 'es' },
	{ label: 'Inglés', value: 'en' },
];

const impactColor: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
	positive: 'success',
	opportunity: 'success',
	caution: 'warning',
	negative: 'error',
	neutral: 'info',
};

const travelBuddyColorMap: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
	green: 'success',
	yellow: 'warning',
	orange: 'warning',
	red: 'error',
	blue: 'info',
};

const travelBuddySeverityMap: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
	green: 'success',
	yellow: 'warning',
	orange: 'warning',
	red: 'error',
	blue: 'info',
};

const countryOptions = (countries as CountryOption[]).slice().sort((a, b) => a.name_es.localeCompare(b.name_es));

const normalizeSearchText = (value: string): string =>
	value
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');

const findCountryByCode = (code?: string): CountryOption | null => {
	if (!code) return null;
	return countryOptions.find((country) => country.code === code.toUpperCase()) || null;
};

const getCountryLabel = (country: CountryOption): string => `${country.code} - ${country.name_es}`;

const getCountrySearchText = (country: CountryOption): string =>
	normalizeSearchText(Object.values(country).filter(Boolean).join(' '));

const countryMatchesQuery = (country: CountryOption, rawQuery: string): boolean => {
	const queryParts = normalizeSearchText(rawQuery).split(/\s+/).filter(Boolean);
	if (!queryParts.length) return true;

	const searchText = getCountrySearchText(country);
	return queryParts.every((part) => searchText.includes(part));
};

const getFilteredCountries = (rawQuery: string): CountryOption[] =>
	countryOptions.filter((country) => countryMatchesQuery(country, rawQuery));

const findExactCountry = (rawValue: string): CountryOption | null => {
	const value = normalizeSearchText(rawValue.trim());
	if (!value) return null;

	return (
		findCountryByCode(rawValue) ||
		countryOptions.find((country) =>
			Object.values(country)
				.filter(Boolean)
				.some((countryValue) => normalizeSearchText(String(countryValue)) === value),
		) ||
		null
	);
};

const toUppercase = (value: string): string => value.toUpperCase();

const splitIsoList = (value: string): string[] =>
	value
		.split(',')
		.map((item) => item.trim().toUpperCase())
		.filter(Boolean);

const compactObject = (payload: Record<string, unknown>): Record<string, unknown> =>
	Object.fromEntries(
		Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== ''),
	);

const toNumberOrUndefined = (value: unknown): number | undefined => {
	if (value === '' || value === undefined || value === null) return undefined;
	const numberValue = Number(value);
	return Number.isNaN(numberValue) ? undefined : numberValue;
};

const getDestinationQuery = (destination?: DestinationOption): Record<string, unknown> => ({
	lat: destination?.lat,
	lon: destination?.lon,
	destinationName: destination?.name,
});

const withoutDestination = (values: FormValues): FormValues => {
	const rest = { ...values };
	delete rest.destination;
	return rest;
};

const getResultCard = (result: any): any => result?.card;

const stringifyStable = (value: unknown): string => JSON.stringify(value, null, 2);

const copyText = async (text?: string): Promise<void> => {
	if (!text) return;
	await navigator.clipboard.writeText(text);
};

const normalizeWarning = (warning: any): string => {
	if (!warning) return '';
	if (typeof warning === 'string') return warning;
	if (typeof warning.message === 'string') return warning.message;
	return stringifyStable(warning);
};

const getWarnings = (result: any): string[] => {
	const metadata = getMetadata(result);
	const warnings = [
		...(Array.isArray(result?.warnings) ? result.warnings : []),
		...(Array.isArray(result?.tripWarnings) ? result.tripWarnings : []),
		...(Array.isArray(metadata?.warnings) ? metadata.warnings : []),
		...(Array.isArray(metadata?.tripWarnings) ? metadata.tripWarnings : []),
	];

	return warnings
		.map(normalizeWarning)
		.filter(Boolean)
		.filter((warning, index, list) => list.indexOf(warning) === index);
};

const getMetadata = (result: any): any => result?.card?.metadata || {};

const getValueFromResult = (result: any, key: string): any => result?.[key] ?? getMetadata(result)?.[key];

const getPrimaryRule = (result: any): PrimaryRule | null => getValueFromResult(result, 'primaryRule') || null;

const formatValue = (value: any): string => {
	if (value === undefined || value === null || value === '') return '';
	if (Array.isArray(value)) return value.filter(Boolean).join(', ');
	if (typeof value === 'object') return stringifyStable(value);
	return String(value);
};

const getPrimaryRuleText = (result: any): string => {
	const primaryRule = getPrimaryRule(result);
	if (!primaryRule) return '';

	const parts = [primaryRule.name, primaryRule.duration].filter(Boolean);
	return parts.join(' · ');
};

const getDestinationInfoText = (result: any): string => {
	const destinationInfo = getValueFromResult(result, 'destinationInfo');
	if (!destinationInfo) return '';

	const parts = [
		destinationInfo.name,
		destinationInfo.capital ? `Capital: ${destinationInfo.capital}` : undefined,
		destinationInfo.currencyCode ? `Moneda: ${destinationInfo.currencyCode}` : undefined,
	].filter(Boolean);
	return parts.join(' · ');
};

const getImportantFacts = (result: any): ImportantFact[] =>
	[
		{
			label: 'Estado del trámite',
			value: getValueFromResult(result, 'statusLabel') || getValueFromResult(result, 'status'),
			highlight: true,
		},
		{ label: 'Regla de visa', value: getPrimaryRuleText(result), highlight: true },
		{ label: 'Pasaporte del viajero', value: getValueFromResult(result, 'passportCountry') },
		{ label: 'Destino', value: getValueFromResult(result, 'destinationCountry') },
		{ label: 'Motivo', value: getValueFromResult(result, 'purpose') },
		{ label: 'Estadía', value: getValueFromResult(result, 'stayDuration') },
		{ label: 'Validez pasaporte', value: getValueFromResult(result, 'passportValidity') },
		{ label: 'Destino info', value: getDestinationInfoText(result) },
		{ label: 'Fuente revisada', value: getValueFromResult(result, 'sourceCheckedAt') },
		{ label: 'Vence', value: getValueFromResult(result, 'expiresAt') },
		{ label: 'Proveedor generó', value: getValueFromResult(result, 'providerGeneratedAt') },
	]
		.map((fact) => ({ ...fact, value: formatValue(fact.value) }))
		.filter((fact) => fact.value);

const getPassengerRequirements = (result: any): any[] => {
	const metadata = getMetadata(result);
	if (Array.isArray(result?.passengers)) return result.passengers;
	if (Array.isArray(result?.passengerRequirements)) return result.passengerRequirements;
	if (Array.isArray(metadata?.passengerRequirements)) return metadata.passengerRequirements;
	return [];
};

const getOfficialLinks = (result: any, includePassengerLinks = true): OfficialLink[] => {
	const metadata = getMetadata(result);
	const passengerLinks = includePassengerLinks
		? getPassengerRequirements(result).flatMap((passenger) =>
				(Array.isArray(passenger?.officialOrProviderLinks) ? passenger.officialOrProviderLinks : []).map(
					(link: OfficialLink) => ({
						...link,
						label: passenger?.passengerRef ? `${passenger.passengerRef} · ${link.label}` : link.label,
					}),
				),
		  )
		: [];
	const links = [
		...(Array.isArray(result?.officialOrProviderLinks) ? result.officialOrProviderLinks : []),
		...(Array.isArray(metadata?.officialOrProviderLinks) ? metadata.officialOrProviderLinks : []),
		...passengerLinks,
	];
	const embassyUrl = result?.destinationInfo?.embassyUrl || metadata?.destinationInfo?.embassyUrl;
	if (embassyUrl) links.push({ label: 'Embajada / fuente oficial', url: embassyUrl });

	return links
		.filter((link) => link?.url)
		.filter((link, index, list) => list.findIndex((item) => item.url === link.url) === index)
		.map((link) => ({ label: link.label || link.url, url: link.url }));
};

const getSellerCopyText = (result: any): string | undefined => {
	const card = getResultCard(result);
	return card?.clientFriendlyText || result?.summary || card?.agentText;
};

const getHighlights = (result: any): any[] => {
	const metadata = getMetadata(result);
	if (Array.isArray(result?.highlights)) return result.highlights;
	if (Array.isArray(metadata?.highlights)) return metadata.highlights;
	return [];
};

const getHighlightCategories = (highlight: any): string[] =>
	[
		...(Array.isArray(highlight?.categoryLabels) ? highlight.categoryLabels : []),
		...(Array.isArray(highlight?.categories) ? highlight.categories : []),
		...(Array.isArray(highlight?.kinds) ? highlight.kinds : []),
	]
		.filter(Boolean)
		.filter((category, index, list) => list.indexOf(category) === index)
		.slice(0, 4);

const formatDistanceMeters = (distanceMeters?: number): string => {
	if (typeof distanceMeters !== 'number' || Number.isNaN(distanceMeters)) return '';
	if (distanceMeters < 1000) return `${Math.round(distanceMeters)} m`;
	return `${(distanceMeters / 1000).toFixed(1)} km`;
};

const truncateText = (value?: string, maxLength = 260): string => {
	if (!value) return '';
	if (value.length <= maxLength) return value;
	return `${value.slice(0, maxLength).trim()}...`;
};

const getHighlightCopyText = (highlight: any, destinationName?: string): string => {
	const distance = formatDistanceMeters(highlight?.distanceMeters);
	const description = highlight?.description || 'atractivo recomendado para sumar contexto al viaje.';
	const parts = [
		`${highlight?.name || 'Atractivo del destino'}${destinationName ? ` en ${destinationName}` : ''}${
			distance ? ` (${distance})` : ''
		}.`,
		truncateText(description, 420),
		highlight?.url ? `Más info: ${highlight.url}` : undefined,
	].filter(Boolean);

	return parts.join('\n');
};

const normalizeMediaImageUrl = (imageUrl?: string): string => {
	if (!imageUrl) return '';

	try {
		const url = new URL(imageUrl);
		const pathParts = url.pathname.split('/');
		const thumbIndex = pathParts.indexOf('thumb');

		if (url.hostname === 'upload.wikimedia.org' && thumbIndex >= 0) {
			url.pathname = pathParts
				.slice(0, thumbIndex)
				.concat(pathParts.slice(thumbIndex + 1, -1))
				.join('/');
			return url.toString();
		}

		return imageUrl;
	} catch {
		return imageUrl;
	}
};

const getEvents = (result: any): any[] => {
	const metadata = getMetadata(result);
	if (Array.isArray(metadata?.events)) return metadata.events;
	if (Array.isArray(result?.events)) return result.events;
	return [];
};

const getEventClassifications = (event: any): string[] =>
	(Array.isArray(event?.classifications) ? event.classifications : []).filter(Boolean).slice(0, 4);

const getEventDateLabel = (event: any): string => {
	if (event?.dateLabel) return event.dateLabel;
	const date = event?.startDate || '';
	const time = event?.startTime ? String(event.startTime).slice(0, 5) : '';
	return [date, time].filter(Boolean).join(' ');
};

const getEventVenueText = (event: any): string => {
	const venue = event?.venue;
	if (!venue) return '';
	return [venue.name, venue.city, venue.countryCode].filter(Boolean).join(' · ');
};

const getEventAddressText = (event: any): string => {
	const venue = event?.venue;
	if (!venue) return '';
	return [venue.address, venue.city, venue.countryCode].filter(Boolean).join(', ');
};

const getEventCopyText = (event: any, destinationName?: string): string => {
	const dateLabel = getEventDateLabel(event);
	const venueText = getEventVenueText(event);
	const description = event?.description || event?.pleaseNote || 'evento público cercano a las fechas del viaje.';
	const parts = [
		`${event?.name || 'Evento'}${destinationName ? ` cerca de ${destinationName}` : ''}${
			dateLabel ? ` · ${dateLabel}` : ''
		}.`,
		venueText ? `Lugar: ${venueText}.` : undefined,
		truncateText(description, 420),
		event?.url ? `Más info: ${event.url}` : undefined,
	].filter(Boolean);

	return parts.join('\n');
};

const getSocialProofEntities = (result: any): any[] => {
	const metadata = getMetadata(result);
	if (Array.isArray(metadata?.entities)) return metadata.entities;
	if (Array.isArray(result?.entities)) return result.entities;
	return [];
};

const getSocialProofEntityUrl = (entity: any): string => entity?.tripadvisorUrl || entity?.url || '';

const getSocialProofEntityAddress = (entity: any): string => entity?.address || entity?.location?.address || '';

const getSocialProofEntityPhotoUrl = (entity: any): string => {
	const photos = Array.isArray(entity?.latestPhotos) ? entity.latestPhotos : [];
	const firstPhoto = photos[0];
	if (!firstPhoto) return '';
	return firstPhoto.url || firstPhoto.imageUrl || firstPhoto.thumbnailUrl || firstPhoto.originalUrl || '';
};

const getSocialProofRatingText = (entity: any): string => {
	const rating = entity?.rating;
	if (rating === undefined || rating === null || rating === '') return '';
	return `${rating}/5`;
};

const getSocialProofReviewCountText = (entity: any): string => {
	const reviewsCount = entity?.reviewsCount;
	if (reviewsCount === undefined || reviewsCount === null || reviewsCount === '') return '';
	return `${reviewsCount} reviews`;
};

const getSocialProofCopyText = (entity: any, destinationName?: string): string => {
	const rating = getSocialProofRatingText(entity);
	const reviews = getSocialProofReviewCountText(entity);
	const address = getSocialProofEntityAddress(entity);
	const url = getSocialProofEntityUrl(entity);
	const latestReviews = Array.isArray(entity?.latestReviews) ? entity.latestReviews : [];
	const firstReview = latestReviews[0];
	const reviewText = firstReview?.text || firstReview?.snippet || firstReview?.title;
	const parts = [
		`${entity?.name || 'Lugar'}${destinationName ? ` cerca de ${destinationName}` : ''}.`,
		[rating, reviews].filter(Boolean).length
			? `Señal pública: ${[rating, reviews].filter(Boolean).join(' · ')}.`
			: undefined,
		address ? `Ubicación: ${address}.` : undefined,
		reviewText ? `Comentario reciente: ${truncateText(reviewText, 260)}` : undefined,
		url ? `Más info: ${url}` : undefined,
	].filter(Boolean);

	return parts.join('\n');
};

const getWeatherMetrics = (result: any): any => {
	const metadata = getMetadata(result);
	return result?.metrics || metadata || null;
};

const hasWeatherResult = (result: any): boolean => {
	const card = getResultCard(result);
	const metrics = getWeatherMetrics(result);
	return (
		card?.type === 'weather' || Boolean(metrics?.avgMaxTempC || metrics?.avgMinTempC || metrics?.rainyDayProbability)
	);
};

const formatTemperature = (value: any): string => {
	if (value === undefined || value === null || value === '') return '';
	return `${Number(value).toFixed(1).replace(/\.0$/, '')} °C`;
};

const formatPercent = (value: any): string => {
	if (value === undefined || value === null || value === '') return '';
	const numericValue = Number(value);
	if (Number.isNaN(numericValue)) return '';
	return `${Math.round(numericValue * 100)}%`;
};

const formatMetricNumber = (value: any, suffix: string): string => {
	if (value === undefined || value === null || value === '') return '';
	const numericValue = Number(value);
	if (Number.isNaN(numericValue)) return '';
	return `${numericValue.toFixed(1).replace(/\.0$/, '')} ${suffix}`;
};

const toolBase = routes.salesTools.root;

const tools: ToolConfig[] = [
	{
		id: 'documentation',
		label: 'Documentación',
		path: routes.salesTools.documentation,
		description: 'Requisitos informativos por pasaporte y destino.',
		endpoint: '/travel-intelligence/documentation/requirements',
		defaultValues: {
			passportCountry: 'UY',
			destinationCountry: '',
			purpose: 'tourism',
			departureDate: '',
			forceRefresh: false,
		},
		fields: [
			{ name: 'passportCountry', label: 'País del pasaporte', type: 'country', required: true },
			{ name: 'destinationCountry', label: 'País destino', type: 'country', required: true },
			{ name: 'purpose', label: 'Motivo', type: 'select', options: purposeOptions },
			{ name: 'departureDate', label: 'Fecha de salida', type: 'date', advanced: true },
			{ name: 'forceRefresh', label: 'Forzar actualización', type: 'switch', advanced: true },
		],
		buildRequest: (values) => ({ query: values }),
	},
	{
		id: 'documentation-check',
		label: 'Chequeo Docs',
		path: routes.salesTools.documentationCheck,
		description: 'Chequeo por viaje y lista de pasajeros.',
		method: 'POST',
		endpoint: '/travel-intelligence/documentation/check',
		defaultValues: {
			originCountry: 'UY',
			destinationCountry: '',
			departureDate: '',
			returnDate: '',
			purpose: 'tourism',
			transitCountries: '',
			travelerDocuments: [
				{ passengerRef: 'pax-1', passportCountry: 'UY', residenceCountry: '', documentType: 'passport', age: '' },
			],
		},
		customForm: DocumentationCheckForm,
		buildRequest: (values) => ({
			body: {
				trip: compactObject({
					originCountry: values.originCountry,
					destinationCountry: values.destinationCountry,
					departureDate: values.departureDate,
					returnDate: values.returnDate,
					purpose: values.purpose,
					transitCountries: splitIsoList(values.transitCountries || ''),
				}),
				travelerDocuments: (values.travelerDocuments || []).map((traveler: TravelerDocument) =>
					compactObject({
						passengerRef: traveler.passengerRef,
						passportCountry: traveler.passportCountry,
						residenceCountry: traveler.residenceCountry,
						documentType: traveler.documentType,
						age: toNumberOrUndefined(traveler.age),
					}),
				),
			},
		}),
	},
	{
		id: 'weather',
		label: 'Clima',
		path: routes.salesTools.weather,
		description: 'Contexto climático histórico del destino.',
		endpoint: '/travel-intelligence/weather/insight',
		defaultValues: { destination: null, periodType: 'month', month: 10, startDate: '', endDate: '', yearsBack: 10 },
		fields: [
			{ name: 'destination', label: 'Destino', type: 'destination', required: true },
			{
				name: 'periodType',
				label: 'Período',
				type: 'select',
				required: true,
				options: [
					{ label: 'Mes', value: 'month' },
					{ label: 'Rango de fechas', value: 'date_range' },
				],
			},
			{
				name: 'month',
				label: 'Mes',
				type: 'number',
				required: true,
				condition: (values) => values.periodType === 'month',
			},
			{
				name: 'startDate',
				label: 'Desde',
				type: 'date',
				required: true,
				condition: (values) => values.periodType === 'date_range',
			},
			{
				name: 'endDate',
				label: 'Hasta',
				type: 'date',
				required: true,
				condition: (values) => values.periodType === 'date_range',
			},
			{ name: 'yearsBack', label: 'Años hacia atrás', type: 'number', advanced: true },
		],
		buildRequest: (values) => ({
			query: compactObject({
				...getDestinationQuery(values.destination),
				periodType: values.periodType,
				month: values.periodType === 'month' ? values.month : undefined,
				startDate: values.periodType === 'date_range' ? values.startDate : undefined,
				endDate: values.periodType === 'date_range' ? values.endDate : undefined,
				yearsBack: values.yearsBack,
			}),
		}),
	},
	{
		id: 'money',
		label: 'Moneda',
		path: routes.salesTools.money,
		description: 'Tipo de cambio y contexto de moneda.',
		endpoint: '/travel-intelligence/money/context',
		defaultValues: { baseCurrency: 'USD', targetCurrency: '', originCountry: '', destinationCountry: '', amount: 100 },
		fields: [
			{ name: 'baseCurrency', label: 'Moneda base', type: 'text', uppercase: true },
			{ name: 'targetCurrency', label: 'Moneda destino', type: 'text', uppercase: true },
			{ name: 'amount', label: 'Monto', type: 'number' },
			{ name: 'originCountry', label: 'País origen', type: 'country', advanced: true },
			{ name: 'destinationCountry', label: 'País destino', type: 'country', advanced: true },
		],
		buildRequest: (values) => ({ query: values }),
	},
	{
		id: 'highlights',
		label: 'Atractivos',
		path: routes.salesTools.highlights,
		description: 'Atractivos públicos cercanos al destino.',
		endpoint: '/travel-intelligence/destinations/highlights',
		defaultValues: { destination: null, language: 'es', radiusMeters: '', limit: 8 },
		fields: [
			{ name: 'destination', label: 'Destino', type: 'destination', required: true },
			{ name: 'language', label: 'Idioma', type: 'select', options: languageOptions },
			{ name: 'limit', label: 'Cantidad', type: 'number' },
			{ name: 'radiusMeters', label: 'Radio en metros', type: 'number', advanced: true },
		],
		buildRequest: (values) => ({
			query: compactObject({ ...getDestinationQuery(values.destination), ...withoutDestination(values) }),
		}),
	},
	{
		id: 'destination-summary',
		label: 'Resumen',
		path: routes.salesTools.destinationSummary,
		description: 'Resumen público breve del destino.',
		endpoint: '/travel-intelligence/destinations/summary',
		defaultValues: {
			destinationName: '',
			destinationCode: '',
			countryCode: '',
			region: '',
			language: 'es',
			forceRefresh: false,
		},
		fields: [
			{ name: 'destinationName', label: 'Destino', type: 'text', required: true },
			{ name: 'countryCode', label: 'País', type: 'country' },
			{ name: 'language', label: 'Idioma', type: 'select', options: languageOptions },
			{ name: 'destinationCode', label: 'Código destino', type: 'text', advanced: true, uppercase: true },
			{ name: 'region', label: 'Región', type: 'text', advanced: true },
			{ name: 'forceRefresh', label: 'Forzar actualización', type: 'switch', advanced: true },
		],
		buildRequest: (values) => ({ query: values }),
	},
	{
		id: 'events',
		label: 'Eventos',
		path: routes.salesTools.events,
		description: 'Eventos públicos durante una ventana de fechas.',
		endpoint: '/travel-intelligence/events/context',
		defaultValues: {
			destination: null,
			startDate: '',
			endDate: '',
			radiusKm: '',
			limit: 10,
			language: 'es',
			category: '',
			forceRefresh: false,
		},
		fields: [
			{ name: 'destination', label: 'Destino', type: 'destination', required: true },
			{ name: 'startDate', label: 'Desde', type: 'date', required: true },
			{ name: 'endDate', label: 'Hasta', type: 'date', required: true },
			{ name: 'language', label: 'Idioma', type: 'select', options: languageOptions },
			{ name: 'limit', label: 'Cantidad', type: 'number' },
			{ name: 'radiusKm', label: 'Radio en km', type: 'number', advanced: true },
			{ name: 'category', label: 'Categoría', type: 'text', advanced: true },
			{ name: 'forceRefresh', label: 'Forzar actualización', type: 'switch', advanced: true },
		],
		buildRequest: (values) => ({
			query: compactObject({ ...getDestinationQuery(values.destination), ...withoutDestination(values) }),
		}),
	},
	{
		id: 'risk',
		label: 'Riesgo',
		path: routes.salesTools.risk,
		description: 'Alertas públicas y riesgo cercano.',
		endpoint: '/travel-intelligence/risk/context',
		defaultValues: { destination: null, radiusKm: 500, daysBack: 14, limit: 20, forceRefresh: false },
		fields: [
			{ name: 'destination', label: 'Destino', type: 'destination', required: true },
			{ name: 'radiusKm', label: 'Radio en km', type: 'number' },
			{ name: 'daysBack', label: 'Días hacia atrás', type: 'number' },
			{ name: 'limit', label: 'Cantidad', type: 'number' },
			{ name: 'forceRefresh', label: 'Forzar actualización', type: 'switch', advanced: true },
		],
		buildRequest: (values) => ({
			query: compactObject({ ...getDestinationQuery(values.destination), ...withoutDestination(values) }),
		}),
	},
	{
		id: 'social-proof',
		label: 'Social Proof',
		path: routes.salesTools.socialProof,
		description: 'Validación pública de viajeros vía Tripadvisor.',
		endpoint: '/travel-intelligence/social-proof/tripadvisor',
		defaultValues: {
			destination: null,
			category: 'attractions',
			language: 'es',
			radiusMeters: '',
			limit: 5,
			enrichment: 'none',
			enrichmentLimit: '',
		},
		fields: [
			{ name: 'destination', label: 'Destino', type: 'destination', required: true },
			{
				name: 'category',
				label: 'Categoría',
				type: 'select',
				options: [
					{ label: 'Atracciones', value: 'attractions' },
					{ label: 'Restaurantes', value: 'restaurants' },
					{ label: 'Hoteles', value: 'hotels' },
					{ label: 'Todo', value: 'all' },
				],
			},
			{ name: 'language', label: 'Idioma', type: 'select', options: languageOptions },
			{ name: 'limit', label: 'Cantidad', type: 'number' },
			{
				name: 'enrichment',
				label: 'Enriquecimiento',
				type: 'select',
				advanced: true,
				options: [
					{ label: 'Sin enriquecimiento', value: 'none' },
					{ label: 'Fotos', value: 'photos' },
					{ label: 'Reviews', value: 'reviews' },
					{ label: 'Completo', value: 'full' },
				],
			},
			{ name: 'enrichmentLimit', label: 'Límite enriquecimiento', type: 'number', advanced: true },
			{ name: 'radiusMeters', label: 'Radio en metros', type: 'number', advanced: true },
		],
		buildRequest: (values) => ({
			query: compactObject({ ...getDestinationQuery(values.destination), ...withoutDestination(values) }),
		}),
	},
];

function DestinationPicker({
	value,
	onChange,
	label,
}: {
	value?: DestinationOption | null;
	onChange: (destination: DestinationOption | null) => void;
	label: string;
}): JSX.Element {
	const [options, setOptions] = useState<DestinationOption[]>(frequentDestinations);
	const [inputValue, setInputValue] = useState('');
	const [loading, setLoading] = useState(false);
	const [searchError, setSearchError] = useState<string | null>(null);
	const selectedValue = typeof value === 'object' ? value : null;

	useEffect(() => {
		let isActive = true;
		const query = inputValue.trim();

		const timer = window.setTimeout(async () => {
			setSearchError(null);
			if (!query) {
				setOptions(frequentDestinations);
				return;
			}

			setLoading(true);
			try {
				const results = await geocodingClient.searchDestinations(query);
				if (!isActive) return;
				setOptions(results);
				if (query.length >= 2 && results.length === 0) {
					setSearchError('No encontramos destinos para esa búsqueda.');
				}
			} catch {
				if (!isActive) return;
				setOptions(frequentDestinations);
				setSearchError('No pudimos buscar destinos. Podés usar un preset o pegar lat,lon.');
			} finally {
				if (isActive) setLoading(false);
			}
		}, 300);

		return () => {
			isActive = false;
			window.clearTimeout(timer);
		};
	}, [inputValue]);

	return (
		<Stack spacing={1}>
			<Autocomplete
				options={options}
				value={selectedValue}
				onChange={(_event, option) => onChange(option)}
				inputValue={inputValue}
				onInputChange={(_event, nextInputValue, reason) => {
					if (reason === 'reset') return;
					setInputValue(nextInputValue);
				}}
				loading={loading}
				getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
				isOptionEqualToValue={(option, selected) =>
					Boolean(option?.label && selected?.label && option.label === selected.label)
				}
				renderOption={(props, option) => (
					<li {...props} key={`${option.type || 'destination'}-${option.label}-${option.lat}-${option.lon}`}>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="body2" sx={{ fontWeight: 800 }}>
								{option.name}
								{option.countryCode ? `, ${option.countryCode}` : ''}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{option.lat}, {option.lon}
								{option.source ? ` · ${option.source}` : ''}
								{option.confidence ? ` · ${option.confidence}` : ''}
								{option.type === 'preset' ? ' · preset' : ''}
							</Typography>
						</Box>
					</li>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						required
						helperText={searchError || 'Buscá un destino por nombre, elegí un preset o pegá lat,lon.'}
						error={Boolean(searchError)}
					/>
				)}
			/>
			{selectedValue && (
				<Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
					<Typography variant="body2" sx={{ fontWeight: 700 }}>
						Destino seleccionado
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{selectedValue.label} · {selectedValue.lat}, {selectedValue.lon}
					</Typography>
				</Paper>
			)}
		</Stack>
	);
}

function CountryAutocomplete({
	label,
	value,
	onChange,
	required,
	helperText,
}: {
	label: string;
	value?: string;
	onChange: (countryCode: string) => void;
	required?: boolean;
	helperText?: string;
}): JSX.Element {
	const selectedCountry = findCountryByCode(value);
	const [inputValue, setInputValue] = useState(selectedCountry ? getCountryLabel(selectedCountry) : value || '');

	useEffect(() => {
		setInputValue(selectedCountry ? getCountryLabel(selectedCountry) : value || '');
	}, [selectedCountry, value]);

	const commitInputValue = (rawValue: string): boolean => {
		const exactCountry = findExactCountry(rawValue);
		const filteredCountries = getFilteredCountries(rawValue);
		const countryToSelect = exactCountry || (filteredCountries.length === 1 ? filteredCountries[0] : null);

		if (!countryToSelect) return false;

		onChange(countryToSelect.code);
		setInputValue(getCountryLabel(countryToSelect));
		return true;
	};

	return (
		<Autocomplete
			autoHighlight
			openOnFocus
			options={countryOptions}
			value={selectedCountry}
			inputValue={inputValue}
			onInputChange={(_event, nextInputValue) => {
				setInputValue(nextInputValue);
			}}
			onChange={(_event, option) => {
				onChange(option?.code || '');
				setInputValue(option ? getCountryLabel(option) : '');
			}}
			getOptionLabel={(option) => (typeof option === 'string' ? option : getCountryLabel(option))}
			isOptionEqualToValue={(option, selected) => option.code === selected.code}
			filterOptions={(options, state) => {
				if (!state.inputValue) return options;
				return options.filter((country) => countryMatchesQuery(country, state.inputValue));
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					helperText={helperText || 'Escribí el código ISO o buscá por nombre.'}
					onKeyDown={(event) => {
						if (event.key === 'Enter' && commitInputValue(inputValue)) {
							event.preventDefault();
						}
					}}
					onBlur={(event) => {
						commitInputValue(event.target.value);
					}}
				/>
			)}
		/>
	);
}

function CountryMultiAutocomplete({
	label,
	value,
	onChange,
	helperText,
}: {
	label: string;
	value?: string;
	onChange: (countryCodes: string) => void;
	helperText?: string;
}): JSX.Element {
	const selectedCountries = splitIsoList(value || '')
		.map(findCountryByCode)
		.filter(Boolean) as CountryOption[];
	const [inputValue, setInputValue] = useState('');

	const addCountryFromInput = (rawValue: string): boolean => {
		const exactCountry = findExactCountry(rawValue);
		const filteredCountries = getFilteredCountries(rawValue);
		const countryToAdd = exactCountry || (filteredCountries.length === 1 ? filteredCountries[0] : null);
		if (!countryToAdd) return false;

		const nextCodes = Array.from(new Set([...selectedCountries.map((country) => country.code), countryToAdd.code]));
		onChange(nextCodes.join(','));
		setInputValue('');
		return true;
	};

	return (
		<Autocomplete
			multiple
			freeSolo
			autoHighlight
			openOnFocus
			selectOnFocus
			clearOnBlur
			handleHomeEndKeys
			options={countryOptions}
			value={selectedCountries}
			inputValue={inputValue}
			onInputChange={(_event, nextInputValue) => setInputValue(nextInputValue)}
			onChange={(_event, options) => {
				const nextCodes = options
					.map((option) => (typeof option === 'string' ? findExactCountry(option)?.code : option.code))
					.filter(Boolean) as string[];
				onChange(Array.from(new Set(nextCodes)).join(','));
				setInputValue('');
			}}
			getOptionLabel={(option) => (typeof option === 'string' ? option : getCountryLabel(option))}
			isOptionEqualToValue={(option, selected) => option.code === selected.code}
			filterOptions={(options, state) => {
				if (!state.inputValue) return options;
				return options.filter((country) => countryMatchesQuery(country, state.inputValue));
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					inputProps={{
						...params.inputProps,
						placeholder: selectedCountries.length ? 'Agregar otro país' : 'Escribí código o país',
					}}
					label={label}
					helperText={helperText || 'Escribí códigos o países y agregalos con Enter.'}
					onKeyDown={(event) => {
						if (event.key === 'Enter' && addCountryFromInput(inputValue)) {
							event.preventDefault();
						}
					}}
				/>
			)}
		/>
	);
}

function CopyButton({ label, value }: { label: string; value?: string }): JSX.Element {
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		await copyText(value);
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1600);
	};

	return (
		<Button
			size="small"
			variant="outlined"
			startIcon={<ContentCopyIcon />}
			onClick={handleCopy}
			disabled={!value}
			sx={{ minWidth: 0, whiteSpace: 'nowrap' }}
		>
			{copied ? 'Copiado' : label}
		</Button>
	);
}

function CopyIconButton({ label, value }: { label: string; value?: string }): JSX.Element {
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		await copyText(value);
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1600);
	};

	return (
		<Tooltip title={copied ? 'Copiado' : label}>
			<span>
				<IconButton size="small" onClick={handleCopy} disabled={!value} aria-label={label}>
					<ContentCopyIcon fontSize="small" />
				</IconButton>
			</span>
		</Tooltip>
	);
}

function ResultMediaImage({ src, alt }: { src?: string; alt: string }): JSX.Element {
	const normalizedSrc = normalizeMediaImageUrl(src);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		setFailed(false);
	}, [normalizedSrc]);

	if (!normalizedSrc || failed) {
		return (
			<Box
				sx={{
					height: 150,
					bgcolor: 'grey.100',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: 'text.secondary',
				}}
			>
				<TravelExploreIcon />
			</Box>
		);
	}

	return (
		<Box
			component="img"
			src={normalizedSrc}
			alt={alt}
			referrerPolicy="no-referrer"
			onError={() => setFailed(true)}
			sx={{
				width: '100%',
				height: 150,
				objectFit: 'cover',
				bgcolor: 'grey.100',
			}}
		/>
	);
}

function renderField(field: ToolField, values: FormValues, setValue: (name: string, value: any) => void): JSX.Element {
	const value = values[field.name] ?? '';

	if (field.type === 'destination') {
		return (
			<DestinationPicker
				key={field.name}
				label={field.label}
				value={value}
				onChange={(destination) => setValue(field.name, destination)}
			/>
		);
	}

	if (field.type === 'country') {
		return (
			<CountryAutocomplete
				key={field.name}
				label={field.label}
				value={value}
				required={field.required}
				helperText={field.helperText}
				onChange={(countryCode) => setValue(field.name, countryCode)}
			/>
		);
	}

	if (field.type === 'select') {
		return (
			<FormControl key={field.name} fullWidth required={field.required}>
				<InputLabel>{field.label}</InputLabel>
				<Select label={field.label} value={value} onChange={(event) => setValue(field.name, event.target.value)}>
					{(field.options || []).map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		);
	}

	if (field.type === 'switch') {
		return (
			<FormControlLabel
				key={field.name}
				control={<Switch checked={Boolean(value)} onChange={(event) => setValue(field.name, event.target.checked)} />}
				label={field.label}
			/>
		);
	}

	return (
		<TextField
			key={field.name}
			fullWidth
			required={field.required}
			type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
			label={field.label}
			value={value}
			helperText={field.helperText}
			InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
			onChange={(event) => {
				const nextValue = field.uppercase ? toUppercase(event.target.value) : event.target.value;
				setValue(field.name, nextValue);
			}}
		/>
	);
}

function DefaultToolForm({ tool, values, setValue }: ToolFormProps & { tool: ToolConfig }): JSX.Element {
	const visibleFields = (tool.fields || []).filter((field) => !field.condition || field.condition(values));
	const basicFields = visibleFields.filter((field) => !field.advanced);
	const advancedFields = visibleFields.filter((field) => field.advanced);

	return (
		<Stack spacing={2}>
			{basicFields.map((field) => renderField(field, values, setValue))}
			{tool.id === 'social-proof' && values.enrichment !== 'none' && (
				<Alert severity="warning">
					Este modo puede generar más llamadas al proveedor externo. Usar solo cuando realmente se necesita.
				</Alert>
			)}
			{advancedFields.length > 0 && (
				<Accordion disableGutters>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography variant="body2" sx={{ fontWeight: 700 }}>
							Opciones avanzadas
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Stack spacing={2}>{advancedFields.map((field) => renderField(field, values, setValue))}</Stack>
					</AccordionDetails>
				</Accordion>
			)}
		</Stack>
	);
}

function DocumentationCheckForm({ values, setValue }: ToolFormProps): JSX.Element {
	const travelers: TravelerDocument[] = values.travelerDocuments || [];

	const updateTraveler = (index: number, field: keyof TravelerDocument, value: string) => {
		const nextTravelers = [...travelers];
		nextTravelers[index] = {
			...nextTravelers[index],
			[field]: field.includes('Country') ? value.toUpperCase() : value,
		};
		setValue('travelerDocuments', nextTravelers);
	};

	const addTraveler = () => {
		setValue('travelerDocuments', [
			...travelers,
			{
				passengerRef: `pax-${travelers.length + 1}`,
				passportCountry: '',
				residenceCountry: '',
				documentType: 'passport',
				age: '',
			},
		]);
	};

	const removeTraveler = (index: number) => {
		setValue(
			'travelerDocuments',
			travelers.filter((_traveler, travelerIndex) => travelerIndex !== index),
		);
	};

	return (
		<Stack spacing={2}>
			<CountryAutocomplete
				label="País origen"
				value={values.originCountry}
				onChange={(countryCode) => setValue('originCountry', countryCode)}
			/>
			<CountryAutocomplete
				required
				label="País destino"
				value={values.destinationCountry}
				onChange={(countryCode) => setValue('destinationCountry', countryCode)}
			/>
			<FormControl fullWidth>
				<InputLabel>Motivo</InputLabel>
				<Select label="Motivo" value={values.purpose} onChange={(event) => setValue('purpose', event.target.value)}>
					{purposeOptions.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6}>
					<TextField
						fullWidth
						type="date"
						label="Salida"
						value={values.departureDate}
						InputLabelProps={{ shrink: true }}
						onChange={(event) => setValue('departureDate', event.target.value)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextField
						fullWidth
						type="date"
						label="Regreso"
						value={values.returnDate}
						InputLabelProps={{ shrink: true }}
						onChange={(event) => setValue('returnDate', event.target.value)}
					/>
				</Grid>
			</Grid>
			<CountryMultiAutocomplete
				label="Países de tránsito"
				helperText="Separados por coma, por ejemplo BR, CL."
				value={values.transitCountries}
				onChange={(countryCodes) => setValue('transitCountries', countryCodes)}
			/>
			<Divider />
			<Stack spacing={1}>
				<Typography variant="subtitle2">Pasajeros</Typography>
				{travelers.map((traveler, index) => (
					<Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
						<Stack spacing={1.5}>
							<Grid container spacing={1.5}>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										label="Referencia"
										value={traveler.passengerRef}
										onChange={(event) => updateTraveler(index, 'passengerRef', event.target.value)}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<CountryAutocomplete
										required
										label="País pasaporte"
										value={traveler.passportCountry}
										onChange={(countryCode) => updateTraveler(index, 'passportCountry', countryCode)}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<CountryAutocomplete
										label="País residencia"
										value={traveler.residenceCountry}
										onChange={(countryCode) => updateTraveler(index, 'residenceCountry', countryCode)}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth>
										<InputLabel>Documento</InputLabel>
										<Select
											label="Documento"
											value={traveler.documentType}
											onChange={(event) => updateTraveler(index, 'documentType', String(event.target.value))}
										>
											<MenuItem value="passport">Pasaporte</MenuItem>
											<MenuItem value="identity_card">Cédula</MenuItem>
											<MenuItem value="other">Otro</MenuItem>
											<MenuItem value="unknown">No definido</MenuItem>
										</Select>
									</FormControl>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										type="number"
										label="Edad"
										value={traveler.age}
										onChange={(event) => updateTraveler(index, 'age', event.target.value)}
									/>
								</Grid>
							</Grid>
							<Button
								size="small"
								color="error"
								onClick={() => removeTraveler(index)}
								disabled={travelers.length === 1}
							>
								Quitar pasajero
							</Button>
						</Stack>
					</Paper>
				))}
				<Button variant="outlined" onClick={addTraveler}>
					Agregar pasajero
				</Button>
			</Stack>
		</Stack>
	);
}

function isSubmitDisabled(tool: ToolConfig, values: FormValues): boolean {
	if (tool.id === 'documentation-check') {
		const travelers: TravelerDocument[] = values.travelerDocuments || [];
		return !values.destinationCountry || travelers.some((traveler) => !traveler.passportCountry);
	}

	return (tool.fields || [])
		.filter((field) => field.required && (!field.condition || field.condition(values)))
		.some((field) => !values[field.name]);
}

function ImportantFactsPanel({ result }: { result: any }): JSX.Element | null {
	const facts = getImportantFacts(result);
	if (!facts.length) return null;

	return (
		<Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
			<Stack spacing={1.5}>
				<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
					Datos clave
				</Typography>
				<Grid container spacing={1.5}>
					{facts.map((fact) => (
						<Grid key={fact.label} item xs={12} sm={6}>
							<Typography variant="caption" color="text.secondary">
								{fact.label}
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: fact.highlight ? 900 : 700 }}>
								{fact.value}
							</Typography>
						</Grid>
					))}
				</Grid>
			</Stack>
		</Paper>
	);
}

function TravelBuddySignalsPanel({ result }: { result: any }): JSX.Element | null {
	const primaryRule = getPrimaryRule(result);
	const statusLabel = getValueFromResult(result, 'statusLabel') || getValueFromResult(result, 'status');
	const stayDuration = getValueFromResult(result, 'stayDuration');
	const passportValidity = getValueFromResult(result, 'passportValidity');
	const primaryRuleColor = primaryRule?.color || '';
	const severity = travelBuddySeverityMap[primaryRuleColor] || 'info';
	const chipColor = travelBuddyColorMap[primaryRuleColor] || 'default';

	if (!primaryRule && !statusLabel) return null;

	return (
		<Alert severity={severity}>
			<Stack spacing={1}>
				<Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
					{statusLabel && <Chip size="small" color="info" label={`Estado: ${statusLabel}`} />}
					{primaryRule?.name && <Chip size="small" color={chipColor} label={primaryRule.name} />}
					{primaryRule?.duration && <Chip size="small" label={`Estadía: ${primaryRule.duration}`} />}
				</Stack>
				<Typography variant="body2">
					{primaryRule?.name
						? `Regla de visa: ${getPrimaryRuleText(result)}.`
						: `Estado informado por el proveedor: ${statusLabel}.`}
					{passportValidity ? ` Pasaporte: ${passportValidity}.` : ''}
					{stayDuration && !primaryRule?.duration ? ` Estadía: ${stayDuration}.` : ''}
				</Typography>
			</Stack>
		</Alert>
	);
}

function PassengerRequirementsPanel({ result }: { result: any }): JSX.Element | null {
	const passengers = getPassengerRequirements(result);
	if (!passengers.length) return null;

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={1.5}>
				<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
					Chequeo por pasajero
				</Typography>
				{passengers.map((passenger, index) => {
					const primaryRule = getPrimaryRule(passenger);
					const primaryRuleColor = primaryRule?.color || '';
					const chipColor = travelBuddyColorMap[primaryRuleColor] || 'default';
					const links = getOfficialLinks(passenger);

					return (
						<Paper
							key={`${passenger.passengerRef || 'passenger'}-${index}`}
							variant="outlined"
							sx={{ p: 1.5, bgcolor: 'grey.50' }}
						>
							<Stack spacing={1}>
								<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
									<Typography variant="body2" sx={{ fontWeight: 900 }}>
										{passenger.passengerRef || `Pasajero ${index + 1}`}
									</Typography>
									{passenger.passportCountry && <Chip size="small" label={`Pasaporte ${passenger.passportCountry}`} />}
									{passenger.statusLabel && <Chip size="small" color="info" label={passenger.statusLabel} />}
									{primaryRule?.name && <Chip size="small" color={chipColor} label={primaryRule.name} />}
								</Stack>
								<Grid container spacing={1}>
									{passenger.stayDuration && (
										<Grid item xs={12} sm={6}>
											<Typography variant="caption" color="text.secondary">
												Estadía
											</Typography>
											<Typography variant="body2" sx={{ fontWeight: 700 }}>
												{passenger.stayDuration}
											</Typography>
										</Grid>
									)}
									{passenger.passportValidity && (
										<Grid item xs={12} sm={6}>
											<Typography variant="caption" color="text.secondary">
												Validez pasaporte
											</Typography>
											<Typography variant="body2" sx={{ fontWeight: 700 }}>
												{passenger.passportValidity}
											</Typography>
										</Grid>
									)}
								</Grid>
								{links.length > 0 && (
									<Stack direction="row" spacing={1} flexWrap="wrap">
										{links.map((link) => (
											<Stack key={link.url} direction="row" spacing={0.5} alignItems="center">
												<Button
													href={link.url}
													target="_blank"
													rel="noopener noreferrer"
													variant="outlined"
													size="small"
													endIcon={<OpenInNewIcon />}
												>
													{link.label}
												</Button>
												<CopyIconButton label="Copiar link" value={link.url} />
											</Stack>
										))}
									</Stack>
								)}
							</Stack>
						</Paper>
					);
				})}
			</Stack>
		</Paper>
	);
}

function OfficialLinksPanel({
	result,
	includePassengerLinks = true,
}: {
	result: any;
	includePassengerLinks?: boolean;
}): JSX.Element | null {
	const links = getOfficialLinks(result, includePassengerLinks);
	if (!links.length) return null;

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={1}>
				<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
					Fuentes para validar
				</Typography>
				<Stack spacing={1}>
					{links.map((link) => (
						<Paper key={link.url} variant="outlined" sx={{ p: 1.25, bgcolor: 'grey.50' }}>
							<Stack
								direction={{ xs: 'column', sm: 'row' }}
								spacing={1}
								justifyContent="space-between"
								alignItems={{ xs: 'stretch', sm: 'center' }}
							>
								<Box sx={{ minWidth: 0 }}>
									<Typography variant="body2" sx={{ fontWeight: 800 }}>
										{link.label}
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
									>
										{link.url}
									</Typography>
								</Box>
								<Stack direction="row" spacing={1}>
									<Button
										href={link.url}
										target="_blank"
										rel="noopener noreferrer"
										variant="outlined"
										size="small"
										endIcon={<OpenInNewIcon />}
									>
										Abrir
									</Button>
									<CopyIconButton label="Copiar link" value={link.url} />
								</Stack>
							</Stack>
						</Paper>
					))}
				</Stack>
			</Stack>
		</Paper>
	);
}

function HighlightsPanel({ result }: { result: any }): JSX.Element | null {
	const highlights = getHighlights(result);
	if (!highlights.length) return null;

	const metadata = getMetadata(result);
	const card = getResultCard(result);
	const destinationName = result?.destinationName || metadata?.destinationName;
	const talkingPoints = Array.isArray(result?.talkingPoints)
		? result.talkingPoints
		: Array.isArray(metadata?.talkingPoints)
		? metadata.talkingPoints
		: [];
	const goodFor = Array.isArray(result?.goodFor)
		? result.goodFor
		: Array.isArray(metadata?.goodFor)
		? metadata.goodFor
		: [];
	const cautionPoints = Array.isArray(result?.cautionPoints)
		? result.cautionPoints
		: Array.isArray(metadata?.cautionPoints)
		? metadata.cautionPoints
		: [];

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={2}>
				<Box>
					<Typography variant="h6" sx={{ fontWeight: 800 }}>
						{card?.title || 'Atractivos del destino'}
					</Typography>
					{card?.clientFriendlyText && (
						<Typography color="text.secondary" sx={{ mt: 0.5 }}>
							{card.clientFriendlyText}
						</Typography>
					)}
				</Box>

				{(talkingPoints.length > 0 || goodFor.length > 0) && (
					<Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
						<Stack spacing={1}>
							{talkingPoints.map((point: string, index: number) => (
								<Typography key={`${point}-${index}`} variant="body2">
									{point}
								</Typography>
							))}
							{goodFor.length > 0 && (
								<Stack direction="row" spacing={1} flexWrap="wrap">
									<Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
										Ideal para
									</Typography>
									{goodFor.map((item: string) => (
										<Chip key={item} size="small" color="success" variant="outlined" label={item} />
									))}
								</Stack>
							)}
						</Stack>
					</Paper>
				)}

				<Grid container spacing={1.5}>
					{highlights.map((highlight, index) => {
						const distance = formatDistanceMeters(highlight?.distanceMeters);
						const categories = getHighlightCategories(highlight);
						const description = truncateText(highlight?.description, 260);

						return (
							<Grid key={`${highlight?.name || 'highlight'}-${index}`} item xs={12} sm={6}>
								<Paper variant="outlined" sx={{ height: '100%', overflow: 'hidden' }}>
									<Stack sx={{ height: '100%' }}>
										<ResultMediaImage src={highlight?.imageUrl} alt={highlight?.name || 'Atractivo del destino'} />
										<Stack spacing={1} sx={{ p: 1.5, flexGrow: 1 }}>
											<Box>
												<Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.25 }}>
													{highlight?.name || `Atractivo ${index + 1}`}
												</Typography>
												<Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
													{distance && <Chip size="small" label={distance} />}
													{highlight?.rate !== undefined && (
														<Chip size="small" label={`relevancia ${highlight.rate}`} />
													)}
													{highlight?.source && <Chip size="small" variant="outlined" label={highlight.source} />}
												</Stack>
											</Box>
											{categories.length > 0 && (
												<Stack direction="row" spacing={0.75} flexWrap="wrap">
													{categories.map((category) => (
														<Chip key={category} size="small" color="info" variant="outlined" label={category} />
													))}
												</Stack>
											)}
											<Typography
												variant="body2"
												color={description ? 'text.primary' : 'text.secondary'}
												sx={{ flexGrow: 1 }}
											>
												{description ||
													'Sin descripción disponible. Útil como punto cercano para investigar o mencionar con cautela.'}
											</Typography>
											<Stack direction="row" spacing={1} flexWrap="wrap">
												{highlight?.url && (
													<Button
														href={highlight.url}
														target="_blank"
														rel="noopener noreferrer"
														variant="outlined"
														size="small"
														endIcon={<OpenInNewIcon />}
													>
														Abrir fuente
													</Button>
												)}
												<CopyButton label="Copiar texto" value={getHighlightCopyText(highlight, destinationName)} />
											</Stack>
										</Stack>
									</Stack>
								</Paper>
							</Grid>
						);
					})}
				</Grid>

				{cautionPoints.length > 0 && (
					<Alert severity="warning">
						<Stack spacing={0.5}>
							{cautionPoints.map((point: string, index: number) => (
								<Typography key={`${point}-${index}`} variant="body2">
									{point}
								</Typography>
							))}
						</Stack>
					</Alert>
				)}
			</Stack>
		</Paper>
	);
}

function EventsPanel({ result }: { result: any }): JSX.Element | null {
	const events = getEvents(result);
	if (!events.length) return null;

	const metadata = getMetadata(result);
	const card = getResultCard(result);
	const destinationName = result?.destinationName || metadata?.destinationName;
	const talkingPoints = Array.isArray(result?.talkingPoints)
		? result.talkingPoints
		: Array.isArray(metadata?.talkingPoints)
		? metadata.talkingPoints
		: [];
	const urgencySignals = Array.isArray(result?.urgencySignals)
		? result.urgencySignals
		: Array.isArray(metadata?.urgencySignals)
		? metadata.urgencySignals
		: [];
	const cautionPoints = Array.isArray(result?.cautionPoints)
		? result.cautionPoints
		: Array.isArray(metadata?.cautionPoints)
		? metadata.cautionPoints
		: [];
	const categoryMix = Array.isArray(metadata?.categoryMix) ? metadata.categoryMix : [];
	const dateRange = metadata?.dateRange;

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={2}>
				<Box>
					<Typography variant="h6" sx={{ fontWeight: 800 }}>
						{card?.title || 'Eventos en fechas cercanas'}
					</Typography>
					{card?.clientFriendlyText && (
						<Typography color="text.secondary" sx={{ mt: 0.5 }}>
							{card.clientFriendlyText}
						</Typography>
					)}
				</Box>

				<Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
					<Stack spacing={1}>
						<Stack direction="row" spacing={1} flexWrap="wrap">
							{typeof metadata?.eventsCount === 'number' && (
								<Chip size="small" color="info" label={`${metadata.eventsCount} eventos`} />
							)}
							{metadata?.eventDensity && (
								<Chip size="small" color="warning" label={`densidad ${metadata.eventDensity}`} />
							)}
							{dateRange?.startDate && dateRange?.endDate && (
								<Chip size="small" label={`${dateRange.startDate} a ${dateRange.endDate}`} />
							)}
						</Stack>
						{talkingPoints.map((point: string, index: number) => (
							<Typography key={`${point}-${index}`} variant="body2">
								{point}
							</Typography>
						))}
						{categoryMix.length > 0 && (
							<Stack direction="row" spacing={1} flexWrap="wrap">
								<Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
									Mezcla
								</Typography>
								{categoryMix.map((category: string) => (
									<Chip key={category} size="small" color="info" variant="outlined" label={category} />
								))}
							</Stack>
						)}
					</Stack>
				</Paper>

				{urgencySignals.length > 0 && (
					<Alert severity="info">
						<Stack spacing={0.5}>
							{urgencySignals.map((signal: string, index: number) => (
								<Typography key={`${signal}-${index}`} variant="body2">
									{signal}
								</Typography>
							))}
						</Stack>
					</Alert>
				)}

				<Grid container spacing={1.5}>
					{events.map((event, index) => {
						const dateLabel = getEventDateLabel(event);
						const venueText = getEventVenueText(event);
						const addressText = getEventAddressText(event);
						const classifications = getEventClassifications(event);
						const description = truncateText(event?.description || event?.pleaseNote, 260);

						return (
							<Grid
								key={`${event?.providerEventId || event?.id || event?.name || 'event'}-${index}`}
								item
								xs={12}
								sm={6}
							>
								<Paper variant="outlined" sx={{ height: '100%', overflow: 'hidden' }}>
									<Stack sx={{ height: '100%' }}>
										<ResultMediaImage src={event?.imageUrl} alt={event?.name || 'Evento'} />
										<Stack spacing={1} sx={{ p: 1.5, flexGrow: 1 }}>
											<Box>
												<Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.25 }}>
													{event?.name || `Evento ${index + 1}`}
												</Typography>
												<Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
													{dateLabel && <Chip size="small" color="success" label={dateLabel} />}
													{event?.source && <Chip size="small" variant="outlined" label={event.source} />}
												</Stack>
											</Box>
											{venueText && (
												<Box>
													<Typography variant="caption" color="text.secondary">
														Lugar
													</Typography>
													<Typography variant="body2" sx={{ fontWeight: 700 }}>
														{venueText}
													</Typography>
													{addressText && (
														<Typography variant="caption" color="text.secondary">
															{addressText}
														</Typography>
													)}
												</Box>
											)}
											{classifications.length > 0 && (
												<Stack direction="row" spacing={0.75} flexWrap="wrap">
													{classifications.map((classification) => (
														<Chip
															key={classification}
															size="small"
															color="info"
															variant="outlined"
															label={classification}
														/>
													))}
												</Stack>
											)}
											<Typography
												variant="body2"
												color={description ? 'text.primary' : 'text.secondary'}
												sx={{ flexGrow: 1 }}
											>
												{description ||
													'Sin descripción disponible. Usar como señal contextual y validar detalle antes de prometer.'}
											</Typography>
											{event?.promoterName && (
												<Typography variant="caption" color="text.secondary">
													Promotor: {event.promoterName}
												</Typography>
											)}
											<Stack direction="row" spacing={1} flexWrap="wrap">
												{event?.url && (
													<Button
														href={event.url}
														target="_blank"
														rel="noopener noreferrer"
														variant="outlined"
														size="small"
														endIcon={<OpenInNewIcon />}
													>
														Abrir evento
													</Button>
												)}
												<CopyButton label="Copiar texto" value={getEventCopyText(event, destinationName)} />
											</Stack>
										</Stack>
									</Stack>
								</Paper>
							</Grid>
						);
					})}
				</Grid>

				{cautionPoints.length > 0 && (
					<Alert severity="warning">
						<Stack spacing={0.5}>
							{cautionPoints.map((point: string, index: number) => (
								<Typography key={`${point}-${index}`} variant="body2">
									{point}
								</Typography>
							))}
						</Stack>
					</Alert>
				)}
			</Stack>
		</Paper>
	);
}

function SocialProofPanel({ result }: { result: any }): JSX.Element | null {
	const entities = getSocialProofEntities(result);
	if (!entities.length) return null;

	const metadata = getMetadata(result);
	const card = getResultCard(result);
	const destinationName = result?.destinationName || metadata?.destinationName;
	const talkingPoints = Array.isArray(result?.talkingPoints)
		? result.talkingPoints
		: Array.isArray(metadata?.talkingPoints)
		? metadata.talkingPoints
		: [];
	const confidenceSignals = Array.isArray(result?.confidenceSignals)
		? result.confidenceSignals
		: Array.isArray(metadata?.confidenceSignals)
		? metadata.confidenceSignals
		: [];
	const cautionPoints = Array.isArray(result?.cautionPoints)
		? result.cautionPoints
		: Array.isArray(metadata?.cautionPoints)
		? metadata.cautionPoints
		: [];
	const costControl = result?.costControl || metadata?.costControl;

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={2}>
				<Box>
					<Typography variant="h6" sx={{ fontWeight: 800 }}>
						{card?.title || 'Validación pública de viajeros'}
					</Typography>
					{card?.clientFriendlyText && (
						<Typography color="text.secondary" sx={{ mt: 0.5 }}>
							{card.clientFriendlyText}
						</Typography>
					)}
				</Box>

				<Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
					<Stack spacing={1}>
						<Stack direction="row" spacing={1} flexWrap="wrap">
							{typeof metadata?.entitiesCount === 'number' && (
								<Chip size="small" color="info" label={`${metadata.entitiesCount} lugares`} />
							)}
							{typeof metadata?.strongEntitiesCount === 'number' && (
								<Chip size="small" color="success" label={`${metadata.strongEntitiesCount} fuerte`} />
							)}
							{typeof metadata?.limitedEntitiesCount === 'number' && (
								<Chip size="small" color="warning" label={`${metadata.limitedEntitiesCount} limitada`} />
							)}
							{metadata?.enrichment && <Chip size="small" label={`enrichment: ${metadata.enrichment}`} />}
						</Stack>
						{talkingPoints.map((point: string, index: number) => (
							<Typography key={`${point}-${index}`} variant="body2">
								{point}
							</Typography>
						))}
						{confidenceSignals.length > 0 && (
							<Stack direction="row" spacing={1} flexWrap="wrap">
								<Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
									Señales
								</Typography>
								{confidenceSignals.map((signal: string) => (
									<Chip key={signal} size="small" color="success" variant="outlined" label={signal} />
								))}
							</Stack>
						)}
					</Stack>
				</Paper>

				<Grid container spacing={1.5}>
					{entities.map((entity, index) => {
						const rating = getSocialProofRatingText(entity);
						const reviewsCount = getSocialProofReviewCountText(entity);
						const address = getSocialProofEntityAddress(entity);
						const url = getSocialProofEntityUrl(entity);
						const latestReviews = Array.isArray(entity?.latestReviews) ? entity.latestReviews : [];
						const latestPhotos = Array.isArray(entity?.latestPhotos) ? entity.latestPhotos : [];
						const review = latestReviews[0];
						const reviewText = review?.text || review?.snippet || review?.title;

						return (
							<Grid
								key={`${entity?.providerEntityId || entity?.id || entity?.name || 'entity'}-${index}`}
								item
								xs={12}
								sm={6}
							>
								<Paper variant="outlined" sx={{ height: '100%', overflow: 'hidden' }}>
									<Stack sx={{ height: '100%' }}>
										<ResultMediaImage src={getSocialProofEntityPhotoUrl(entity)} alt={entity?.name || 'Lugar'} />
										<Stack spacing={1} sx={{ p: 1.5, flexGrow: 1 }}>
											<Box>
												<Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.25 }}>
													{entity?.name || `Lugar ${index + 1}`}
												</Typography>
												<Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
													{rating && <Chip size="small" color="success" label={rating} />}
													{reviewsCount && <Chip size="small" label={reviewsCount} />}
													{entity?.classification && (
														<Chip
															size="small"
															color={entity.classification === 'strong' ? 'success' : 'warning'}
															label={entity.classification}
														/>
													)}
													{entity?.source && <Chip size="small" variant="outlined" label={entity.source} />}
												</Stack>
											</Box>
											{address && (
												<Box>
													<Typography variant="caption" color="text.secondary">
														Ubicación
													</Typography>
													<Typography variant="body2" sx={{ fontWeight: 700 }}>
														{address}
													</Typography>
												</Box>
											)}
											<Typography
												variant="body2"
												color={reviewText ? 'text.primary' : 'text.secondary'}
												sx={{ flexGrow: 1 }}
											>
												{reviewText
													? truncateText(reviewText, 260)
													: 'Sin reviews enriquecidas en esta consulta. Usar como señal pública, no como recomendación fuerte.'}
											</Typography>
											{latestPhotos.length > 0 && (
												<Typography variant="caption" color="text.secondary">
													{latestPhotos.length} foto{latestPhotos.length === 1 ? '' : 's'} disponible
													{latestReviews.length > 0
														? ` · ${latestReviews.length} review${latestReviews.length === 1 ? '' : 's'}`
														: ''}
												</Typography>
											)}
											<Stack direction="row" spacing={1} flexWrap="wrap">
												{url && (
													<Button
														href={url}
														target="_blank"
														rel="noopener noreferrer"
														variant="outlined"
														size="small"
														endIcon={<OpenInNewIcon />}
													>
														Abrir Tripadvisor
													</Button>
												)}
												<CopyButton label="Copiar texto" value={getSocialProofCopyText(entity, destinationName)} />
											</Stack>
										</Stack>
									</Stack>
								</Paper>
							</Grid>
						);
					})}
				</Grid>

				{costControl && (
					<Typography variant="caption" color="text.secondary">
						Uso proveedor: nearby {costControl.nearbyRequests || 0}, reviews {costControl.reviewRequests || 0}, fotos{' '}
						{costControl.photoRequests || 0}.
					</Typography>
				)}

				{cautionPoints.length > 0 && (
					<Alert severity="warning">
						<Stack spacing={0.5}>
							{cautionPoints.map((point: string, index: number) => (
								<Typography key={`${point}-${index}`} variant="body2">
									{point}
								</Typography>
							))}
						</Stack>
					</Alert>
				)}
			</Stack>
		</Paper>
	);
}

function WeatherPanel({ result }: { result: any }): JSX.Element | null {
	if (!hasWeatherResult(result)) return null;

	const card = getResultCard(result);
	const metrics = getWeatherMetrics(result) || {};
	const period = result?.period;
	const classification = result?.classification || {};
	const destinationName = result?.destinationName || getMetadata(result)?.destinationName;
	const metricItems = [
		{ label: 'Máxima promedio', value: formatTemperature(metrics.avgMaxTempC), color: 'warning' },
		{ label: 'Mínima promedio', value: formatTemperature(metrics.avgMinTempC), color: 'info' },
		{ label: 'Media promedio', value: formatTemperature(metrics.avgMeanTempC), color: 'default' },
		{ label: 'Probabilidad lluvia', value: formatPercent(metrics.rainyDayProbability), color: 'primary' },
		{ label: 'Lluvia promedio', value: formatMetricNumber(metrics.avgPrecipitationMmPerDay, 'mm/día'), color: 'info' },
		{ label: 'Días de lluvia', value: formatMetricNumber(metrics.avgRainyDaysPerPeriod, 'días'), color: 'info' },
		{ label: 'Viento máximo', value: formatMetricNumber(metrics.avgWindMaxKmh, 'km/h'), color: 'default' },
	].filter((item) => item.value);
	const classificationItems = [
		{ label: 'general', value: classification.overall || getMetadata(result)?.classification },
		{ label: 'temperatura', value: classification.temperature },
		{ label: 'lluvia', value: classification.rain },
		{ label: 'viento', value: classification.wind },
	].filter((item) => item.value);

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={2}>
				<Box>
					<Typography variant="h6" sx={{ fontWeight: 800 }}>
						{card?.title || 'Clima histórico del destino'}
					</Typography>
					{card?.clientFriendlyText && (
						<Typography color="text.secondary" sx={{ mt: 0.5 }}>
							{card.clientFriendlyText}
						</Typography>
					)}
				</Box>

				<Stack direction="row" spacing={1} flexWrap="wrap">
					{destinationName && <Chip size="small" color="info" label={destinationName} />}
					{period?.label && <Chip size="small" label={period.label} />}
					{period?.historicalStartDate && period?.historicalEndDate && (
						<Chip size="small" label={`${period.historicalStartDate} a ${period.historicalEndDate}`} />
					)}
				</Stack>

				<Grid container spacing={1.5}>
					{metricItems.map((item) => (
						<Grid key={item.label} item xs={12} sm={6} md={4}>
							<Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', height: '100%' }}>
								<Typography variant="caption" color="text.secondary">
									{item.label}
								</Typography>
								<Typography variant="h6" sx={{ fontWeight: 900 }}>
									{item.value}
								</Typography>
							</Paper>
						</Grid>
					))}
				</Grid>

				{classificationItems.length > 0 && (
					<Stack direction="row" spacing={1} flexWrap="wrap">
						{classificationItems.map((item) => (
							<Chip key={item.label} size="small" variant="outlined" label={`${item.label}: ${item.value}`} />
						))}
					</Stack>
				)}

				{card?.agentText && <Alert severity={card.impact === 'caution' ? 'warning' : 'info'}>{card.agentText}</Alert>}
			</Stack>
		</Paper>
	);
}

function ResultPanel({
	result,
	error,
	loading,
	isDirty,
}: {
	result?: unknown;
	error?: TravelIntelligenceError | null;
	loading: boolean;
	isDirty: boolean;
}): JSX.Element {
	const card = getResultCard(result);
	const warnings = getWarnings(result);
	const response = result as any;
	const hasHighlights = getHighlights(response).length > 0;
	const hasEvents = getEvents(response).length > 0;
	const hasSocialProof = getSocialProofEntities(response).length > 0;
	const hasWeather = hasWeatherResult(response);

	return (
		<Card variant="outlined" sx={{ minHeight: 420 }}>
			<CardContent>
				<Stack spacing={2}>
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 800 }}>
							Resultado
						</Typography>
						{isDirty && result && <Chip size="small" color="warning" label="Resultado anterior" sx={{ mt: 1 }} />}
					</Box>
					{loading && (
						<Alert severity="info" icon={<CircularProgress size={18} />}>
							Consultando. Mantengo visible el último resultado.
						</Alert>
					)}
					{loading && !result && (
						<Stack spacing={1}>
							<Skeleton variant="rounded" height={120} />
							<Skeleton variant="rounded" height={80} />
						</Stack>
					)}
					{error && (
						<Alert severity="error">
							<Typography variant="body2" sx={{ fontWeight: 700 }}>
								{error.code || 'ERROR'}
							</Typography>
							<Typography variant="body2">{error.message}</Typography>
							{Boolean(error.status) && <Typography variant="caption">HTTP {error.status}</Typography>}
						</Alert>
					)}
					{warnings.map((warning, index) => (
						<Alert key={`${warning}-${index}`} severity="warning">
							{warning}
						</Alert>
					))}
					{result ? (
						<>
							<Stack direction="row" spacing={1} flexWrap="wrap">
								{typeof response.cached === 'boolean' && (
									<Chip size="small" label={response.cached ? 'cached' : 'not cached'} />
								)}
								{typeof response.stale === 'boolean' && (
									<Chip
										size="small"
										color={response.stale ? 'warning' : 'success'}
										label={response.stale ? 'stale' : 'fresh'}
									/>
								)}
								{response.source && <Chip size="small" label={`source: ${response.source}`} />}
								{card?.confidence && <Chip size="small" label={`confidence: ${card.confidence}`} />}
								{card?.impact && (
									<Chip size="small" color={impactColor[card.impact] || 'default'} label={`impact: ${card.impact}`} />
								)}
							</Stack>
							<TravelBuddySignalsPanel result={response} />
							<ImportantFactsPanel result={response} />
							<PassengerRequirementsPanel result={response} />
							<OfficialLinksPanel
								result={response}
								includePassengerLinks={!getPassengerRequirements(response).length}
							/>
							<HighlightsPanel result={response} />
							<EventsPanel result={response} />
							<SocialProofPanel result={response} />
							<WeatherPanel result={response} />
							{card && !hasHighlights && !hasEvents && !hasSocialProof && !hasWeather && (
								<Paper variant="outlined" sx={{ p: 2 }}>
									<Stack spacing={1}>
										<Typography variant="h6" sx={{ fontWeight: 800 }}>
											{card.title || 'Intelligence card'}
										</Typography>
										{card.agentText && <Typography>{card.agentText}</Typography>}
										{card.clientFriendlyText && (
											<Typography color="text.secondary">{card.clientFriendlyText}</Typography>
										)}
										<Stack direction="row" spacing={1} flexWrap="wrap">
											{card.source && <Chip size="small" label={`source: ${card.source}`} />}
											{card.confidence && <Chip size="small" label={`confidence: ${card.confidence}`} />}
											{card.impact && (
												<Chip size="small" color={impactColor[card.impact] || 'default'} label={card.impact} />
											)}
										</Stack>
									</Stack>
								</Paper>
							)}
							{!card && !hasHighlights && !hasEvents && !hasSocialProof && !hasWeather && (
								<Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
									<pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
										{stringifyStable(result)}
									</pre>
								</Paper>
							)}
							<Stack direction="row" spacing={1} flexWrap="wrap" sx={{ '& .MuiButton-root': { flexShrink: 0 } }}>
								<CopyButton label="Copiar texto vendedor" value={getSellerCopyText(response)} />
							</Stack>
							<Accordion disableGutters>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography variant="body2" sx={{ fontWeight: 700 }}>
										JSON completo
									</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Box
										component="pre"
										sx={{
											m: 0,
											p: 2,
											bgcolor: 'grey.100',
											borderRadius: 1,
											overflow: 'auto',
											fontSize: 12,
										}}
									>
										{stringifyStable(result)}
									</Box>
								</AccordionDetails>
							</Accordion>
						</>
					) : (
						!loading && (
							<Alert severity="info">Completá el formulario y consultá una herramienta para ver el resultado.</Alert>
						)
					)}
				</Stack>
			</CardContent>
		</Card>
	);
}

function ToolRunner({ tool }: { tool: ToolConfig }): JSX.Element {
	const [values, setValues] = useState<FormValues>(tool.defaultValues);
	const [result, setResult] = useState<unknown>();
	const [error, setError] = useState<TravelIntelligenceError | null>(null);
	const [loading, setLoading] = useState(false);
	const [lastSubmitted, setLastSubmitted] = useState('');
	const currentSignature = useMemo(() => stringifyStable(values), [values]);
	const isDirty = Boolean(result) && lastSubmitted !== currentSignature;

	const setValue = (name: string, value: any) => {
		setValues((current) => ({ ...current, [name]: value }));
	};

	const handleClear = () => {
		setValues(tool.defaultValues);
		setError(null);
	};

	const handleSubmit = async () => {
		setLoading(true);
		setError(null);
		setLastSubmitted(currentSignature);
		try {
			const request = tool.buildRequest(values);
			const response = await requestTravelIntelligence({
				method: tool.method || 'GET',
				path: tool.endpoint,
				query: request.query,
				body: request.body,
			});
			setResult(response);
		} catch (requestError) {
			setError(requestError as TravelIntelligenceError);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} md={5}>
				<Card variant="outlined">
					<CardContent>
						<Stack spacing={2}>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 800 }}>
									{tool.label}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{tool.description}
								</Typography>
							</Box>
							{tool.customForm ? (
								tool.customForm({ values, setValue })
							) : (
								<DefaultToolForm tool={tool} values={values} setValue={setValue} />
							)}
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
								<Button variant="contained" onClick={handleSubmit} disabled={loading || isSubmitDisabled(tool, values)}>
									Consultar
								</Button>
								<Button variant="outlined" onClick={handleClear} disabled={loading}>
									Limpiar
								</Button>
							</Stack>
						</Stack>
					</CardContent>
				</Card>
			</Grid>
			<Grid item xs={12} md={7}>
				<ResultPanel result={result} error={error} loading={loading} isDirty={isDirty} />
			</Grid>
		</Grid>
	);
}

function SalesToolsTabs({
	activePath,
	onPathChange,
}: {
	activePath: string;
	onPathChange: (path: string) => void;
}): JSX.Element {
	const activeTool = tools.find((tool) => tool.path === activePath) || tools[0];

	return (
		<Tabs
			value={activeTool.path}
			onChange={(_event, value) => onPathChange(value)}
			variant="scrollable"
			scrollButtons="auto"
			allowScrollButtonsMobile
		>
			{tools.map((tool) => (
				<Tab key={tool.id} value={tool.path} label={tool.label} />
			))}
		</Tabs>
	);
}

export default function SalesToolsPage(): JSX.Element {
	const location = useLocation();
	const resolvePath = (path: string): string => {
		if (path === toolBase) return routes.salesTools.documentation;
		return tools.some((tool) => tool.path === path) ? path : routes.salesTools.documentation;
	};

	const [activePath, setActivePath] = useState(() => resolvePath(location.pathname));
	const activeTool = tools.find((tool) => tool.path === activePath) || tools[0];

	useEffect(() => {
		const resolvedPath = resolvePath(window.location.pathname);
		if (resolvedPath !== window.location.pathname) {
			window.history.replaceState({}, '', resolvedPath);
		}
		setActivePath(resolvedPath);

		const handlePopState = () => {
			setActivePath(resolvePath(window.location.pathname));
		};

		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, []);

	const handlePathChange = (path: string) => {
		window.history.pushState({}, '', path);
		setActivePath(path);
	};

	return (
		<Layout>
			<Container maxWidth="xl" sx={{ py: 3 }}>
				<Stack spacing={2}>
					<Box>
						<Stack direction="row" spacing={1} alignItems="center">
							<TravelExploreIcon color="primary" />
							<Typography variant="h5" sx={{ fontWeight: 900 }}>
								Sales Tools
							</Typography>
						</Stack>
						<Typography variant="body2" color="text.secondary">
							Herramientas atómicas de travel intelligence para conversaciones comerciales.
						</Typography>
					</Box>
					<Paper variant="outlined">
						<SalesToolsTabs activePath={activePath} onPathChange={handlePathChange} />
					</Paper>
					<ToolRunner key={activeTool.id} tool={activeTool} />
				</Stack>
			</Container>
		</Layout>
	);
}
